import React from 'react'

import { Button, Fieldset, Input, ModalBox, Select } from '@fourthwall/components'
import { useFormik } from '@fourthwall/utils/dist/formik'
import {
  NON_SHIPPABLE_COUNTRIES,
  STATE_OPTIONS,
  getCountriesOptions,
} from '@fourthwall/utils/dist/inputs'

import type { ShippingLocation } from '../../Shipping.types'
import * as S from './ShippingLocationModal.styled'
import type { FormValues } from './ShippingLocationModal.types'
import { validationSchema } from './consts'

const countriesOptions = getCountriesOptions({
  exclude: NON_SHIPPABLE_COUNTRIES,
})

type ShippingLocationModalProps = {
  shippingLocation?: ShippingLocation
  title: string
  onSubmit: (values: FormValues) => void
  close: () => void
}

export const ShippingLocationModal = ({
  shippingLocation,
  title,
  onSubmit,
  close,
}: ShippingLocationModalProps) => {
  const formik = useFormik<FormValues>({
    initialValues: {
      address1: shippingLocation?.address1 || '',
      address2: shippingLocation?.address2 || '',
      state: shippingLocation?.state || '',
      zip: shippingLocation?.zip || '',
      city: shippingLocation?.city || '',
      country: shippingLocation?.country || '',
    },
    validationSchema,
    onSubmit: (values: FormValues) => {
      close()
      onSubmit(values)
    },
  })

  const stateOptions = STATE_OPTIONS[formik.values.country]

  const renderState = () => {
    const shouldDisplayStateDropdown = !!stateOptions

    if (shouldDisplayStateDropdown) {
      return <Select label="State" options={stateOptions} {...formik.getFieldProps('state')} />
    }

    return <Input label="State" {...formik.getFieldProps('state')} />
  }

  const handleCountryChange = () => {
    formik.setFieldValue('state', '')
    formik.setFieldTouched('state', false)
  }

  return (
    <ModalBox
      size="small"
      onClose={close}
      title={title}
      buttons={[
        <Button label="Cancel" appearance="secondary" onClick={close} />,
        <Button label="Save" appearance="primary" onClick={formik.submitForm} />,
      ]}
    >
      <S.Subtitle>
        This address is used to calculate shipping costs and taxes. Your supporters do not see it.
      </S.Subtitle>
      <form onSubmit={formik.handleSubmit}>
        <Fieldset.Row>
          <Fieldset.Item>
            <Input label="Street address" {...formik.getFieldProps('address1')} />
          </Fieldset.Item>
        </Fieldset.Row>
        <Fieldset.Row>
          <Fieldset.Item>
            <Input
              label="Apartament, suite, etc. (optional)"
              {...formik.getFieldProps('address2')}
            />
          </Fieldset.Item>
        </Fieldset.Row>
        <Fieldset.Row>
          <Fieldset.Item>
            <Input label="City" {...formik.getFieldProps('city')} />
          </Fieldset.Item>
        </Fieldset.Row>
        <Fieldset.Row>
          <Fieldset.Item>
            <Select
              options={countriesOptions}
              label="Country"
              onValueChange={handleCountryChange}
              {...formik.getFieldProps('country')}
            />
          </Fieldset.Item>
        </Fieldset.Row>
        <Fieldset.Row>
          <Fieldset.Item flex={2}>{renderState()}</Fieldset.Item>
          <Fieldset.Item>
            <Input label="Zip code" {...formik.getFieldProps('zip')} />
          </Fieldset.Item>
        </Fieldset.Row>
      </form>
    </ModalBox>
  )
}
